import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { useNavigate } from "react-router";
import { BarChart3, Puzzle } from "lucide-react";

import { Modal } from "./modal";
import { ToolNavItem } from "../../layout/toolNavItems";

type Props = {
    isOpen: boolean;
    onClose: () => void;
    /** The therapist's adopted tools, already filtered — see `visibleToolNavItems`. */
    tools: ToolNavItem[];
};

/**
 * The adopted tools, one tap from the mobile bar.
 *
 * The bottom bar has five slots and the tool catalog keeps growing, so tools
 * cannot each own one. Behind a single button the row stays the same width
 * whether a therapist has adopted one tool or ten.
 *
 * Shaped like the launch sheets the docks already open, so a therapist who
 * has learned one has learned this one.
 */
export default function MobileToolsSheet({ isOpen, onClose, tools }: Props) {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const go = (path: string) => () => {
        onClose();
        navigate(path);
    };

    const entry = (
        onClick: () => void,
        icon: React.ReactNode,
        title: string,
        muted = false
    ) => (
        <button
            key={title}
            type="button"
            onClick={onClick}
            className={`mb-3 flex w-full items-center gap-3 rounded-2xl border p-3 text-left transition-colors ${
                muted
                    ? "border-gray-200 text-gray-600 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800/50"
                    : "border-brand-100 bg-brand-50 text-brand-700 hover:bg-brand-100 dark:border-brand-500/30 dark:bg-brand-500/10 dark:text-brand-300"
            }`}
        >
            <span
                className={`flex h-10 w-10 items-center justify-center rounded-full ${
                    muted
                        ? "bg-gray-100 text-gray-500 dark:bg-gray-800"
                        : "bg-white text-brand-600 shadow-sm dark:bg-gray-800"
                }`}
            >
                {icon}
            </span>
            <span className="flex-1 text-sm font-semibold">{title}</span>
        </button>
    );

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            showCloseButton={false}
            className="mx-4 max-w-lg rounded-3xl"
        >
            <motion.div
                initial={{ y: 24, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.25, ease: "easeOut" }}
                className="flex max-h-[80vh] flex-col overflow-y-auto p-5"
            >
                <div className="mb-4">
                    <h2 className="text-lg font-bold text-gray-800 dark:text-white">
                        {t("mobileTools.title")}
                    </h2>
                    <p className="text-sm text-content-tertiary">
                        {/* Not "you have adopted none": a therapist may hold
                            several tools that are reached from a patient's
                            page rather than from a page of their own. */}
                        {tools.length === 0
                            ? t("mobileTools.emptySubtitle")
                            : t("mobileTools.subtitle")}
                    </p>
                </div>

                {tools.map((tool) => entry(go(`/${tool.path}`), tool.icon, t(tool.name)))}

                {/* Not an adopted tool — every practitioner has their own
                    reports — but the bar has no slot left for it, and this
                    sheet is where the pages beyond the four fixed ones live. */}
                {entry(go("/reports"), <BarChart3 className="h-5 w-5" />, t("sidebar.reports"))}

                {/* Last, and muted: the catalog is where you go when what you
                    wanted is not in the list above. */}
                {entry(
                    go("/tools-catalog"),
                    <Puzzle className="h-5 w-5" />,
                    t("sidebar.toolsCatalog"),
                    true
                )}

                <button
                    type="button"
                    onClick={onClose}
                    className="mt-2 w-full rounded-xl border border-gray-200 py-2.5 text-sm font-semibold text-gray-500 transition-colors hover:bg-gray-50 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-800/50"
                >
                    {t("common.cancel")}
                </button>
            </motion.div>
        </Modal>
    );
}
